import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Lock, RefreshCw, Mail } from "lucide-react";
import SectionLayout from "@/pages/SectionLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "pandurata.admin.key";

// sessionStorage instead of localStorage: the admin key is cleared automatically
// when the browser tab is closed (less surface area for XSS / shared-device leaks).
const safeGetKey = () => {
  try {
    return sessionStorage.getItem(KEY_STORAGE) || "";
  } catch {
    return "";
  }
};

const safeSetKey = (v) => {
  try {
    sessionStorage.setItem(KEY_STORAGE, v);
  } catch {
    /* storage might be disabled — fall through silently */
  }
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const Admin = () => {
  const [key, setKey] = useState(() => safeGetKey());
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("comments");
  const [comments, setComments] = useState([]);
  const [likes, setLikes] = useState({ items: [], counts: {} });

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const [c, l] = await Promise.all([
        axios.get(`${API}/admin/comments`, { params: { key: k } }),
        axios.get(`${API}/admin/likes`, { params: { key: k } }),
      ]);
      setComments(c.data || []);
      setLikes(l.data || { items: [], counts: {} });
      setAuthed(true);
      safeSetKey(k);
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 401 ? "Wrong key." : "Could not load.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitKey = (e) => {
    e.preventDefault();
    if (key.trim()) load(key.trim());
  };

  const logout = () => {
    localStorage.removeItem(KEY_STORAGE);
    setKey("");
    setAuthed(false);
    setComments([]);
    setLikes({ items: [], counts: {} });
  };

  if (!authed) {
    return (
      <SectionLayout>
        <h2 className="glass-panel__title" data-testid="section-title">
          ADMIN
        </h2>
        <p className="glass-panel__body">
          Private. Enter your admin key to view feelings and likes.
        </p>
        <form className="admin-key-form" onSubmit={submitKey}>
          <Lock size={16} strokeWidth={1.6} />
          <input
            type="password"
            className="admin-key-form__input"
            placeholder="Admin key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            autoFocus
            data-testid="admin-key-input"
          />
          <button
            type="submit"
            className="glass-panel__btn"
            disabled={loading || !key.trim()}
            data-testid="admin-key-submit"
          >
            {loading ? "Checking…" : "Enter"}
          </button>
        </form>
        {error && (
          <p className="admin-error" data-testid="admin-error">
            {error}
          </p>
        )}
        <div className="glass-panel__nav">
          <Link to="/" className="glass-panel__btn" data-testid="btn-home">
            Home
          </Link>
        </div>
      </SectionLayout>
    );
  }

  return (
    <SectionLayout>
      <div className="admin" data-testid="admin">
        <header className="admin__head">
          <h2 className="glass-panel__title" data-testid="section-title">
            ADMIN
          </h2>
          <button
            type="button"
            className="admin__refresh"
            onClick={() => load(key)}
            disabled={loading}
            data-testid="admin-refresh"
          >
            <RefreshCw size={14} strokeWidth={1.6} />
            <span>{loading ? "Loading…" : "Refresh"}</span>
          </button>
        </header>

        <div className="admin__tabs" role="tablist">
          <button
            type="button"
            className={`admin__tab ${tab === "comments" ? "admin__tab--active" : ""}`}
            onClick={() => setTab("comments")}
            role="tab"
            aria-selected={tab === "comments"}
            data-testid="admin-tab-comments"
          >
            Feelings ({comments.length})
          </button>
          <button
            type="button"
            className={`admin__tab ${tab === "likes" ? "admin__tab--active" : ""}`}
            onClick={() => setTab("likes")}
            role="tab"
            aria-selected={tab === "likes"}
            data-testid="admin-tab-likes"
          >
            Likes ({likes.items?.length || 0})
          </button>
        </div>

        {tab === "comments" && (
          <div className="admin__list" data-testid="admin-comments">
            {comments.length === 0 ? (
              <p className="admin__empty">No feelings yet.</p>
            ) : (
              comments.map((c) => (
                <article key={c.id} className="admin__card">
                  <header className="admin__card-head">
                    <span className="admin__card-title">{c.image_title}</span>
                    <span className="admin__card-date">{formatDate(c.created_at)}</span>
                  </header>
                  <p className="admin__card-body">{c.text}</p>
                  <a
                    className="admin__card-mail"
                    href={`mailto:${c.commenter_email}`}
                  >
                    <Mail size={12} strokeWidth={1.6} />
                    <span>{c.commenter_email}</span>
                  </a>
                </article>
              ))
            )}
          </div>
        )}

        {tab === "likes" && (
          <div className="admin__list" data-testid="admin-likes">
            <div className="admin__counts">
              {Object.keys(likes.counts).length === 0 ? (
                <p className="admin__empty">No likes yet.</p>
              ) : (
                Object.entries(likes.counts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([imageId, count]) => (
                    <div key={imageId} className="admin__count-row">
                      <span className="admin__count-key">{imageId}</span>
                      <span className="admin__count-value">{count}</span>
                    </div>
                  ))
              )}
            </div>
          </div>
        )}

        <div className="glass-panel__nav admin__footer">
          <button
            type="button"
            className="glass-panel__btn"
            onClick={logout}
            data-testid="admin-logout"
          >
            Sign out
          </button>
          <Link
            to="/"
            className="glass-panel__btn glass-panel__btn--right"
            data-testid="btn-home"
          >
            Home
          </Link>
        </div>
      </div>
    </SectionLayout>
  );
};

export default Admin;
